/**
 * Sentry crash reporting wrapper.
 *
 * Behavior:
 *   • Only initializes when VITE_SENTRY_DSN is set in the environment. This
 *     means dev builds (which usually don't have a DSN) silently no-op, and
 *     the app keeps working in any environment that hasn't been configured.
 *   • Once initialized, captures uncaught exceptions, unhandled promise
 *     rejections, and anything explicitly passed to `captureError`.
 *   • Tags every event with route/release info so we can correlate crashes
 *     to specific deploys via Vercel's commit SHA (if available).
 *
 * Why a wrapper instead of importing Sentry directly at the call sites:
 *   keeps the dependency at a single chokepoint. If we ever swap providers,
 *   only this file changes.
 */

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// EVERY iOS BUILD USED TO CALL ITSELF "dev".
//
// Both env vars below come from Vercel, and the iOS app is not built on
// Vercel — Capacitor wraps a local `npm run build`. So every event from every
// iPhone, from every build ever shipped, arrived tagged `release: dev`:
// 100% of them, in the Aug 16 getLayer issue and in every issue before it.
//
// The cost was not cosmetic. That issue had been "fixed" three times (Aug 1
// mapAlive, Aug 6 removeGoogle + bike lanes, Aug 6 cheap-restyle) and Sentry
// kept marking it Regressed — but with one release name for all builds,
// "regressed" and "these are old builds still in people's hands" are the same
// picture, and nothing could tell them apart. An audit found all 13 getLayer
// call sites already guarded, which means the honest reading of that issue is
// "we cannot say whether this is fixed" — rule 20's unmeasured-looks-like-zero,
// wearing a version number.
//
// __BUILD_SHA__ is `git rev-parse --short HEAD` at build time (vite.config.ts)
// and already prints at startup for exactly this reason — it just never
// reached Sentry. It identifies the CODE, which is the thing a stack trace
// needs to be read against, and it works for web and iOS alike.
declare const __BUILD_SHA__: string;
const BUILD_SHA = typeof __BUILD_SHA__ === "string" ? __BUILD_SHA__ : "unknown";
const RELEASE =
  (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined) ??
  (import.meta.env.VITE_RELEASE as string | undefined) ??
  // Not "dev". A build that cannot name itself cannot be diagnosed.
  (BUILD_SHA !== "unknown" ? BUILD_SHA : "dev");
const ENV =
  (import.meta.env.MODE as string | undefined) ??
  (import.meta.env.VITE_ENV as string | undefined) ??
  "development";

// Set by MainViewController.swift, only in Simulator builds. The bundle is
// always built in production mode, so without this every Simulator error —
// including JS typed into the WebView while driving it by hand — was filed
// as `production` from an iPhone (Sep 30).
const IS_SIMULATOR =
  typeof window !== "undefined" &&
  (window as { __PLEASANT_SIMULATOR__?: boolean }).__PLEASANT_SIMULATOR__ === true;
const SENTRY_ENV = IS_SIMULATOR ? "simulator" : ENV;

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (!DSN) {
    // No DSN configured — keep the app silent. Logging a warning to console
    // during dev is fine, but we don't want it in production noise.
    if (ENV !== "production") {
       
      console.info("[sentry] No VITE_SENTRY_DSN — crash reporting disabled.");
    }
    return;
  }
  try {
    Sentry.init({
      dsn: DSN,
      environment: SENTRY_ENV,
      release: RELEASE,
      // Conservative sampling — we're early; we don't need every breadcrumb.
      // tracesSampleRate captures performance spans (10%). Replays disabled —
      // they're expensive in bytes and we're not ready for that yet.
      tracesSampleRate: SENTRY_ENV === "production" ? 0.1 : 0.0,
      // Don't send events for known-noisy browser issues (extensions, etc).
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
        // Chunk-load failures are handled separately by main.tsx's hard-
        // reset path — no point in alerting on those.
        "Importing a module script failed",
        "Failed to fetch dynamically imported module",
        "Unable to preload CSS",
        // A phone in a tunnel, not a bug (Aug 5, Rochester). This is
        // supabase-js's FunctionsFetchError: the request never LEFT the
        // phone. Every invoke site has a fallback (valhalla → Mapbox, etc.)
        // and the fate logs record it; server-side failures are the daily
        // canary's job. Cellular blips are noise here — never page Morgan.
        "Failed to send a request to the Edge Function",
      ],
      // Safari masks browser-EXTENSION scripts as webkit-masked-url://hidden/.
      // Errors thrown there are someone's password manager, not our bundle
      // (Aug 4: a visitor's extension threw "Can't find variable: data" in
      // its own refreshToken() and paged Morgan). Never report them.
      denyUrls: [/webkit-masked-url/i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],
      // Strip query strings from URLs by default to avoid leaking incoming
      // share/deep-link parameters (Mapbox tokens shouldn't ever land in a
      // URL, but defense in depth).
      beforeSend(event) {
        if (event.request?.url) {
          try {
            const u = new URL(event.request.url);
            event.request.url = `${u.origin}${u.pathname}`;
          } catch {
            /* leave as-is */
          }
        }
        return event;
      },
    });
    initialized = true;
  } catch (err) {
    // Sentry's own init throwing is the one error we definitely don't want
    // to surface — eat it so the app still boots.
     
    console.warn("[sentry] init failed:", err);
  }
}

/**
 * Manually log an error to Sentry. Use this in catch blocks where you'd
 * otherwise just swallow the error. Adds the supplied context as breadcrumbs
 * so we can see what was happening when it went wrong.
 */
export function captureError(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;
  try {
    if (context) {
      Sentry.withScope((scope) => {
        scope.setContext("pleasant", context);
        Sentry.captureException(err);
      });
    } else {
      Sentry.captureException(err);
    }
  } catch {
    /* never let the error reporter throw */
  }
}

/** Convenience re-exports so callers don't import @sentry/react directly. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
